import PropTypes from 'prop-types';
import { Checkbox, Rating } from '@codex/basics';
import styled from 'styled-components';

const COLOR_IMAGES_MAP = {
  'black': 'black',
  'blue': 'blue',
  'brown': 'brown',
  'bronze': 'bronze',
  'chrome': 'chrome',
  'cream': 'cream',
  'dblue': 'dblue',
  'gold': 'gold',
  'grey': 'grey',
  'green': 'green',
  'magenta': 'magenta',
  'multi': 'multi',
  'orange': 'orange',
  'patterned': 'patterned',
  'pink': 'pink',
  'purple': 'purple',
  'red': 'red',
  'silver': 'silver',
  'silver/grey': 'silver-grey',
  'cyan': 'blue',
  'clear': 'white',
  'tri-colour': 'tricolour',
  'black & tri-colour': 'tricolour-black',
  'steel': 'steel',
  'torquise': 'torquise',
  'white': 'white',
  'yellow': 'yellow'
};

const ImageLabel = styled.img`
  width: 16px;
  height: 16px;
  margin-right: 5px;
`;

const LabelAnchor = styled.a`
  display: inline;
  padding: 0;
  text-decoration: none;
  color: ${({ disabled, theme }) => (disabled ? theme.colors.shades[5] : theme.colors.shades[1])};
  :hover {
    color: ${({ disabled, theme }) => (disabled ? theme.colors.shades[5] : theme.colors.shades[1])};
  }
`;

const FilterOption = styled.li`
  input {
    margin-right: 4px;
  }

  @media only screen and (max-width: 1024px) {
    border-top: 1px solid #ccc;
    padding: 10px;
    margin-bottom: 0;

    &:first-child {
      border-top: 1px solid #ccc;
    }

    // TODO: not in fo-app maybe in @codex?
    .dc-checkbox-label {
      padding: 2px 0 0 10px;
      color: #666;
    }
  }
`;

export default function CheckboxList({
  id,
  regularCheckboxesItems,
  checkboxSize,
  toggleCheckboxOption,
  priceFilterCheckboxesItems,
  renderCheckboxLabelsWithLinks
}) {
  function onCheckboxCheck(checkboxData) {
    toggleCheckboxOption({
      filterId: id,
      checkboxData: checkboxData
    });
  }

  function renderRegularFilterCheckboxes() {
    return regularCheckboxesItems.values.map((regularFilterItem) => (
      <FilterOption
        className={'dc-filter__option'}
        key={`${regularFilterItem.label}${regularFilterItem.value}${regularFilterItem.selected}`}
      >
        <Checkbox
          size={checkboxSize}
          disabled={regularFilterItem.count === '0'}
          checked={regularFilterItem.selected}
          onCheck={() => onCheckboxCheck(regularFilterItem)}
          attrs={{
            container: { style: { padding: 0 } },
            box: { style: { marginRight: '5px', boxSizing: 'border-box' } }
          }}
        >
          {renderCheckboxLabelsWithLinks && regularFilterItem.linkToListingPage ? (
            <LabelAnchor
              disabled={regularFilterItem.count === '0'}
              href={regularFilterItem.linkToListingPage}
              onClick={(e) => {
                e.preventDefault();
              }}
            >
              {renderRegularFilterCheckboxLabel(regularFilterItem)}
            </LabelAnchor>
          ) : (
            renderRegularFilterCheckboxLabel(regularFilterItem)
          )}
        </Checkbox>
      </FilterOption>
    ));
  }

  function renderRegularFilterCheckboxLabel(regularFilterItem) {
    return (
      <>
        {regularCheckboxesItems.label === 'Colour' && (
          <ImageLabel
            src={`https://currys-ssl.cdn.dixons.com/css/themes/colour-swatch/Search-colour-icons-${
              COLOR_IMAGES_MAP[regularFilterItem.label.toLowerCase()]
            }.jpg`}
          />
        )}
        {regularFilterItem.label ? regularFilterItem.label : regularFilterItem.value}
        {' '}
        {regularFilterItem.count ? (
          <small>
            (
            {regularFilterItem.count}
            )
          </small>
        ) : null}
      </>
    );
  }

  function renderPriceFilterCheckboxes() {
    return priceFilterCheckboxesItems.map((priceFilterItem) => (
      <FilterOption
        className={'dc-filter__option'}
        key={`${priceFilterItem.label}${priceFilterItem.selected}`}
      >
        <Checkbox
          size={checkboxSize}
          label={priceFilterItem.label}
          checked={priceFilterItem.selected}
          onCheck={() => onCheckboxCheck(priceFilterItem)}
          attrs={{
            container: { style: { padding: 0 } },
            box: { style: { marginRight: '5px', boxSizing: 'border-box' } }
          }}
        >
          {priceFilterItem.label}
        </Checkbox>
      </FilterOption>
    ));
  }

  function renderCustomerRatingFilterCheckboxes() {
    return regularCheckboxesItems.values.map((customerRatingFilterItem) => (
      <FilterOption
        className={'dc-filter__option'}
        key={`${customerRatingFilterItem.label}${customerRatingFilterItem.value}${customerRatingFilterItem.selected}`}
      >
        <Checkbox
          size={checkboxSize}
          disabled={customerRatingFilterItem.count === '0'}
          checked={customerRatingFilterItem.selected}
          onCheck={() => onCheckboxCheck(customerRatingFilterItem)}
          attrs={{
            container: { style: { padding: 0 } },
            box: { style: { marginRight: '5px', boxSizing: 'border-box' } }
          }}
        >
          {renderCheckboxLabelsWithLinks && customerRatingFilterItem.linkToListingPage ? (
            <LabelAnchor
              disabled={customerRatingFilterItem.count === '0'}
              href={customerRatingFilterItem.linkToListingPage}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              {renderCustomerRatingCheckboxLabel(customerRatingFilterItem)}
            </LabelAnchor>
          ) : (
            renderCustomerRatingCheckboxLabel(customerRatingFilterItem)
          )}
        </Checkbox>
      </FilterOption>
    ));
  }

  function renderCustomerRatingCheckboxLabel(customerRatingFilterItem) {
    return (
      <>
        <Rating
          ratingValue={Math.min(
            ...customerRatingFilterItem.value.split(',').map((singleValue) => Number(singleValue))
          )}
          maxCount={5}
          style={{ display: 'inline-block' }}
        />
        <span>
          {` ${customerRatingFilterItem.label} `}
          <small>{`(${customerRatingFilterItem.count})`}</small>
        </span>
      </>
    );
  }

  if (id === 'price') {
    return renderPriceFilterCheckboxes();
  }

  if (id === 'rounded_score') {
    return renderCustomerRatingFilterCheckboxes();
  }

  return renderRegularFilterCheckboxes();
}

CheckboxList.displayName = 'CheckboxList';

CheckboxList.propTypes = {
  id: PropTypes.string.isRequired,
  checkboxSize: PropTypes.oneOf(['small', 'extra-small']).isRequired,
  toggleCheckboxOption: PropTypes.func,
  priceFilterCheckboxesItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      selected: PropTypes.bool.isRequired,
      count: PropTypes.string,
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          lowerRangeInclusive: PropTypes.number.isRequired,
          upperRangeInclusive: PropTypes.number
        })
      ]).isRequired
    })
  ),
  regularCheckboxesItems: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    values: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        selected: PropTypes.bool.isRequired,
        count: PropTypes.string,
        linkToListingPage: PropTypes.string
      })
    ).isRequired
  }),
  renderCheckboxLabelsWithLinks: PropTypes.bool
};

CheckboxList.defaultProps = {
  toggleCheckboxOption: () => {},
  priceFilterCheckboxesItems: undefined,
  regularCheckboxesItems: undefined,
  renderCheckboxLabelsWithLinks: false
};
